import {Application} from '@hotwired/stimulus';
import * as Turbo from '@hotwired/turbo';
import {shouldPerformTransition, performTransition} from 'turbo-view-transitions';

import PackagesController from './controllers/packages-controller';

document.addEventListener('turbo:before-render', event => {
	if (shouldPerformTransition()) {
		event.preventDefault();

		performTransition(document.body, event.detail.newBody, async () => {
			await event.detail.resume();
		});
	}
});

document.addEventListener('turbo:load', () => {
	if (shouldPerformTransition()) Turbo.cache.exemptPageFromCache();
});

window.Stimulus = Application.start();
Stimulus.register('packages', PackagesController);

let currentPageCleanup = null;
let bootedBody = null;

function createPageContext() {
	const abortController = new AbortController();
	const cleanupTasks = [];

	return {
		signal: abortController.signal,
		addCleanup(task) {
			cleanupTasks.push(task);
		},
		cleanup() {
			abortController.abort();
			cleanupTasks.splice(0).forEach(task => task());
		}
	};
}

function bootPage() {
	if (bootedBody === document.body) {
		return;
	}

	teardownPage();
	const page = createPageContext();

	initHeaderState(page);
	initFooterReveal(page);
	initFadeUp(page);
	initCounters(page);
	initHeroCounter(page);
	initPricingToggle(page);
	initFaq(page);
	initSlider(page);
	initSpotlight(page);
	initDotCanvas(page);

	currentPageCleanup = page.cleanup;
	bootedBody = document.body;
}

function teardownPage() {
	currentPageCleanup?.();
	currentPageCleanup = null;
	bootedBody = null;
}

document.addEventListener('DOMContentLoaded', bootPage);
document.addEventListener('turbo:load', bootPage);
document.addEventListener('turbo:before-cache', teardownPage);
document.addEventListener('turbo:before-render', teardownPage);

function easeOutExpo(t) {
	return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const counterAnimations = new WeakMap();

function animateCounter(el, target, prefix, suffix, duration, page) {
	counterAnimations.get(el)?.();
	const finalText = `${prefix || ''}${Number(target) || 0}${suffix || ''}`;
	if (prefersReducedMotion()) {
		el.textContent = finalText;
		return;
	}
	const startTime = performance.now();
	let frameId;
	const cancel = () => {
		cancelAnimationFrame(frameId);
		el.textContent = finalText;
	};
	counterAnimations.set(el, cancel);
	page?.addCleanup(cancel);
	function tick(now) {
		if (page?.signal.aborted) return;
		const progress = Math.min((now - startTime) / duration, 1);
		el.textContent = `${prefix || ''}${Math.round((Number(target) || 0) * easeOutExpo(progress))}${suffix || ''}`;
		if (progress < 1) frameId = requestAnimationFrame(tick);
		else counterAnimations.delete(el);
	}
	frameId = requestAnimationFrame(tick);
}

function initFadeUp(page) {
	const fadeNodes = document.querySelectorAll('.fade-up');

	if (!fadeNodes.length) {
		return;
	}

	if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
		fadeNodes.forEach(node => node.classList.add('is-visible'));
		return;
	}

	const io = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-visible');
					io.unobserve(entry.target);
				}
			});
		},
		{ threshold: 0.1, rootMargin: '0px 0px -20px 0px' }
	);

	fadeNodes.forEach((node) => io.observe(node));
	page.addCleanup(() => io.disconnect());
}

function initCounters(page) {
	const nodes = document.querySelectorAll('.js-cf-counter');
	const finalValue = el => {
		el.textContent = `${el.dataset.prefix || ''}${el.dataset.target || '0'}${el.dataset.suffix || ''}`;
	};
	nodes.forEach(finalValue);
	if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
	const io = new IntersectionObserver(entries => {
		entries.forEach(entry => {
			if (!entry.isIntersecting) return;
			const el = entry.target;
			animateCounter(el, el.dataset.target, el.dataset.prefix, el.dataset.suffix, 1500, page);
			io.unobserve(el);
		});
	}, { threshold: 0.35 });
	nodes.forEach(node => io.observe(node));
	page.addCleanup(() => io.disconnect());
}

function initHeroCounter(page) {
	document.querySelectorAll('.js-cf-hero-counter').forEach(node => {
		animateCounter(node, node.dataset.target || node.textContent, '', '', 1400, page);
	});
}

function initPricingToggle(page) {
	const toggles = document.querySelectorAll('.js-cf-pricing-toggle');

	toggles.forEach((toggle) => {
		const section = toggle.closest('.cf-section');
		if (!section) {
			return;
		}

		const prices = section.querySelectorAll('.js-cf-price');
		const labels = section.querySelectorAll('.js-cf-billing-label');
		const units = section.querySelectorAll('.js-cf-price-unit');
		const thumb = section.querySelector('.cf-switch__thumb');

		toggle.addEventListener('change', () => {
			const annual = toggle.checked;

			if (thumb) {
				thumb.style.transform = annual ? 'translateX(16px)' : '';
				thumb.style.background = annual ? 'var(--cf-accent)' : 'var(--cf-muted)';
			}

			prices.forEach((price) => {
				const from = parseInt(price.textContent, 10);
				const to = annual
					? parseInt(price.dataset.annual, 10)
					: parseInt(price.dataset.monthly, 10);

				animateCounter(price, Number.isNaN(to) ? from : to, '', '', 380, page);
			});

			labels.forEach((label) => {
				label.textContent = annual ? 'billed annually' : 'billed monthly';
			});

			units.forEach((unit) => {
				unit.textContent = annual ? '/year' : '/mo';
			});
		}, { signal: page.signal });
	});
}

function initFaq(page) {
	document.querySelectorAll('.js-cf-faq').forEach(root => {
		const tablist = root.querySelector('.cf-faq__tabs');
		const tabs = [...root.querySelectorAll('.cf-faq__tabs button')];
		const panels = [...root.querySelectorAll('.cf-faq__panel')];
		if (!tabs.length || !tablist) return;
		root.classList.add('is-enhanced');
		tablist.setAttribute('role', 'tablist');
		const select = index => {
			tabs.forEach((tab, i) => {
				tab.setAttribute('role', 'tab');
				tab.setAttribute('aria-selected', String(i === index));
				tab.setAttribute('aria-controls', panels[i].id);
				tab.tabIndex = i === index ? 0 : -1;
				tab.classList.toggle('is-active', i === index);
				panels[i].setAttribute('role', 'tabpanel');
				panels[i].setAttribute('aria-labelledby', tab.id);
				panels[i].hidden = i !== index;
				panels[i].tabIndex = 0;
			});
		};
		select(0);
		tabs.forEach((tab, index) => {
			tab.addEventListener('click', () => select(index), { signal: page.signal });
			tab.addEventListener('keydown', event => {
				let next;
				if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
				if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
				if (event.key === 'Home') next = 0;
				if (event.key === 'End') next = tabs.length - 1;
				if (next === undefined) return;
				event.preventDefault();
				select(next);
				tabs[next].focus();
			}, { signal: page.signal });
		});
		const setExpanded = (item, expanded) => {
			item.classList.toggle('is-open', expanded);
			item.querySelector('button').setAttribute('aria-expanded', String(expanded));
			item.querySelector('.cf-faq__answer').hidden = !expanded;
		};
		root.querySelectorAll('.cf-faq__item').forEach(item => {
			setExpanded(item, false);
			item.querySelector('button').addEventListener('click', () => {
				const expanded = !item.classList.contains('is-open');
				item.closest('.cf-faq__panel').querySelectorAll('.cf-faq__item').forEach(entry => setExpanded(entry, entry === item && expanded));
			}, { signal: page.signal });
		});
		page.addCleanup(() => {
			root.classList.remove('is-enhanced');
			tablist.removeAttribute('role');
			tabs.forEach(tab => {
				['role', 'aria-selected', 'aria-controls', 'tabindex'].forEach(attr => tab.removeAttribute(attr));
				tab.classList.remove('is-active');
			});
			panels.forEach(panel => {
				panel.hidden = false;
				['role', 'aria-labelledby', 'tabindex'].forEach(attr => panel.removeAttribute(attr));
			});
			root.querySelectorAll('.cf-faq__item').forEach(item => setExpanded(item, true));
		});
	});
}

function initSlider(page) {
	document.querySelectorAll('.cf-slider').forEach(slider => {
		const track = slider.querySelector('.js-cf-slider');
		const prev = slider.querySelector('.js-cf-slide-prev');
		const next = slider.querySelector('.js-cf-slide-next');
		if (!track || !prev || !next) return;
		slider.classList.add('is-enhanced');
		track.tabIndex = 0;
		track.setAttribute('role', 'region');
		track.setAttribute('aria-label', 'Product screens');
		const sync = () => {
			prev.disabled = track.scrollLeft <= 1;
			next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
		};
		const move = dir => {
			const width = track.querySelector('.cf-slide')?.getBoundingClientRect().width || track.clientWidth;
			const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
			track.scrollBy({ left: dir * (width + gap), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
		};
		prev.addEventListener('click', () => move(-1), { signal: page.signal });
		next.addEventListener('click', () => move(1), { signal: page.signal });
		track.addEventListener('scroll', sync, { passive: true, signal: page.signal });
		track.addEventListener('keydown', event => {
			if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
			event.preventDefault();
			move(event.key === 'ArrowLeft' ? -1 : 1);
		}, { signal: page.signal });
		window.addEventListener('resize', sync, { passive: true, signal: page.signal });
		sync();
		page.addCleanup(() => {
			slider.classList.remove('is-enhanced');
			prev.disabled = next.disabled = false;
		});
	});
}

function initSpotlight(page) {
	document.querySelectorAll('.js-cf-spotlight').forEach((card) => {
		card.addEventListener('mousemove', (event) => {
			const bounds = card.getBoundingClientRect();
			card.style.setProperty('--mx', `${event.clientX - bounds.left}px`);
			card.style.setProperty('--my', `${event.clientY - bounds.top}px`);
		}, { signal: page.signal });
	});
}

function initDotCanvas(page) {
	document.querySelectorAll('.js-cf-hero').forEach(hero => initHeroCanvas(page, hero));
}

function initHeroCanvas(page, hero) {
	const canvas = hero?.querySelector('.js-cf-dot-canvas');

	if (!hero || !canvas) {
		return;
	}

	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return;
	}

	const SPACING = 26;
	const DOT_BASE = 1.2;
	const RADIUS = 200;

	let width = 0;
	let height = 0;
	let dots = [];
	let mouseX = -1;
	let mouseY = -1;
	let angle = 0;
	let frameId = null;

	function resize() {
		width = canvas.width = hero.offsetWidth;
		height = canvas.height = hero.offsetHeight;
		dots = [];

		for (let x = SPACING / 2; x < width; x += SPACING) {
			for (let y = SPACING / 2; y < height; y += SPACING) {
				dots.push({ x, y, noise: Math.random() });
			}
		}
	}

	function draw() {
		ctx.clearRect(0, 0, width, height);
		angle += 0.006;

		const cx = mouseX >= 0 ? mouseX : width / 2 + Math.cos(angle * 0.8) * width * 0.3;
		const cy = mouseY >= 0 ? mouseY : height * 0.36 + Math.sin(angle) * height * 0.18;

		dots.forEach((dot) => {
			const dx = dot.x - cx;
			const dy = dot.y - cy;
			const dist = Math.sqrt(dx * dx + dy * dy);
			const prox = Math.max(0, 1 - dist / RADIUS);
			const glow = prox * prox;
			const base = 0.04 + dot.noise * 0.04;
			const alpha = base + glow * 0.55;
			const radius = DOT_BASE + glow * 1.8;

			ctx.beginPath();
			ctx.arc(dot.x, dot.y, radius, 0, 2 * Math.PI);
			ctx.fillStyle = glow > 0.08
				? `rgba(96,165,250,${alpha})`
				: `rgba(140,170,200,${alpha})`;
			ctx.fill();
		});

		if (!prefersReducedMotion()) frameId = requestAnimationFrame(draw);
	}

	hero.addEventListener('mousemove', (event) => {
		const rect = canvas.getBoundingClientRect();
		mouseX = event.clientX - rect.left;
		mouseY = event.clientY - rect.top;
	}, { signal: page.signal });

	hero.addEventListener('mouseleave', () => {
		mouseX = -1;
		mouseY = -1;
	}, { signal: page.signal });

	window.addEventListener('resize', resize, { passive: true, signal: page.signal });

	page.addCleanup(() => {
		if (frameId !== null) {
			cancelAnimationFrame(frameId);
		}
	});

	resize();
	draw();
}

function initHeaderState(page) {
	const header = document.getElementById('site-header');

	if (!header) {
		return;
	}

	const sync = () => {
		header.classList.toggle('is-scrolled', window.scrollY > 20);
	};

	const toggle = header.querySelector('.site-header__toggle');
	const navigation = header.querySelector('.site-header__navigation');
	if (toggle && navigation) {
		header.classList.add('is-enhanced');
		const setOpen = open => {
			header.classList.toggle('is-menu-open', open);
			toggle.setAttribute('aria-expanded', String(open));
			toggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
		};
		toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'), { signal: page.signal });
		header.addEventListener('keydown', event => {
			if (event.key === 'Escape' && header.classList.contains('is-menu-open')) {
				setOpen(false);
				toggle.focus();
			}
		}, { signal: page.signal });
		navigation.addEventListener('click', event => {
			if (event.target.closest('a')) setOpen(false);
		}, { signal: page.signal });
		document.addEventListener('click', event => {
			if (!header.contains(event.target)) setOpen(false);
		}, { signal: page.signal });
		header.addEventListener('focusout', event => {
			if (event.relatedTarget && !header.contains(event.relatedTarget)) setOpen(false);
		}, { signal: page.signal });
		window.addEventListener('resize', () => setOpen(false), { passive: true, signal: page.signal });
		page.addCleanup(() => {
			setOpen(false);
			header.classList.remove('is-enhanced');
		});
	}
	sync();
	window.addEventListener('scroll', sync, { passive: true, signal: page.signal });
}

function initFooterReveal(page) {
	const footer = document.getElementById('site-footer');

	if (!footer) {
		return;
	}

	if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
		footer.classList.add('is-revealed');
		return;
	}
	const io = new IntersectionObserver(
		(entries) => {
			if (entries[0]?.isIntersecting) {
				footer.classList.add('is-revealed');
				io.unobserve(footer);
			}
		},
		{ threshold: 0.05 }
	);

	io.observe(footer);
	page.addCleanup(() => io.disconnect());
}
